/**
 * Finite sets of elements typed as text.
 *
 * An element is kept as its text, except that numbers are written one way (`+3`, `3.0`
 * and `3` are the same element) and that nested braces, brackets and brackets keep their
 * commas, so a set of sets — the output of a power set — can be pasted back in.
 * Comparison is case-sensitive: `a` and `A` are different elements.
 */
export interface ParsedSet {
  elements: string[];
  /** How many repeated entries were dropped. */
  duplicates: number;
}

const NUMBER = /^[-+]?(?:\d+\.?\d*|\.\d+)$/;
const OPENERS = "{[(";
const CLOSERS = "}])";
const SEPARATORS = /[,;\n]/;
const WHITESPACE = /\s+/g;
const LEADING_PLUS = /^\+/;
const OUTER_BRACES = /^\{([\s\S]*)\}$/;
const QUOTED = /^(["'])(.*)\1$/;

/** Split at commas, semicolons and line breaks that are not inside brackets. */
function splitTopLevel(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const ch of text) {
    if (OPENERS.includes(ch)) {
      depth += 1;
    } else if (CLOSERS.includes(ch)) {
      depth = Math.max(0, depth - 1);
    }
    if (depth === 0 && SEPARATORS.test(ch)) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts;
}

/** One element's canonical text. */
export function normalizeElement(raw: string): string {
  const text = raw.trim().replace("−", "-");
  const quoted = QUOTED.exec(text);
  if (quoted) {
    return quoted[2];
  }
  if (NUMBER.test(text)) {
    const value = Number(text.replace(LEADING_PLUS, ""));
    return Number.isFinite(value) && Math.abs(value) < 1e15
      ? String(value === 0 ? 0 : value)
      : text.replace(LEADING_PLUS, "");
  }
  if (OPENERS.includes(text[0] ?? "")) {
    return text.replace(WHITESPACE, "").replace(/,/g, ", ");
  }
  return text.replace(WHITESPACE, " ");
}

const collator = new Intl.Collator("en", { numeric: true });

function isNumeric(element: string): boolean {
  return NUMBER.test(element);
}

/** Numbers first in numeric order, then everything else in natural text order. */
export function compareElements(a: string, b: string): number {
  const na = isNumeric(a);
  const nb = isNumeric(b);
  if (na && nb) {
    return Number(a) - Number(b);
  }
  if (na !== nb) {
    return na ? -1 : 1;
  }
  return collator.compare(a, b);
}

export function sortElements(elements: readonly string[]): string[] {
  return [...elements].sort(compareElements);
}

/** Read `{1, 2, 3}`, `1 2 3`, `red, green` or one element per line. */
export function parseSet(input: string): ParsedSet {
  let text = input.trim();
  const braces = OUTER_BRACES.exec(text);
  if (braces) {
    text = braces[1];
  }
  const parts = SEPARATORS.test(text)
    ? splitTopLevel(text)
    : text.split(WHITESPACE);
  const seen = new Set<string>();
  let total = 0;
  for (const part of parts) {
    if (part.trim() === "") {
      continue;
    }
    total += 1;
    seen.add(normalizeElement(part));
  }
  return { elements: sortElements([...seen]), duplicates: total - seen.size };
}

export const EMPTY_SET = "∅";

/** `{1, 2, 3}` or `∅`. */
export function formatSet(elements: readonly string[]): string {
  return elements.length === 0 ? EMPTY_SET : `{${elements.join(", ")}}`;
}

export function union(a: readonly string[], b: readonly string[]): string[] {
  return sortElements([...new Set([...a, ...b])]);
}

export function intersection(
  a: readonly string[],
  b: readonly string[]
): string[] {
  const inB = new Set(b);
  return a.filter((x) => inB.has(x));
}

export function difference(
  a: readonly string[],
  b: readonly string[]
): string[] {
  const inB = new Set(b);
  return a.filter((x) => !inB.has(x));
}

export function symmetricDifference(
  a: readonly string[],
  b: readonly string[]
): string[] {
  return sortElements([...difference(a, b), ...difference(b, a)]);
}

export function isSubset(a: readonly string[], b: readonly string[]): boolean {
  const inB = new Set(b);
  return a.every((x) => inB.has(x));
}

export function pairText(a: string, b: string): string {
  return `(${a}, ${b})`;
}

export const MAX_POWER_SET_LISTED = 7;
export const MAX_PRODUCT_LISTED = 100;

/** Every subset, smallest first, each in the order of the original set. */
export function powerSet(elements: readonly string[]): string[][] {
  const subsets: string[][] = [];
  const n = elements.length;
  for (let mask = 0; mask < 2 ** n; mask += 1) {
    subsets.push(
      elements.filter((_, i) => Math.floor(mask / 2 ** i) % 2 === 1)
    );
  }
  return subsets.sort(
    (x, y) => x.length - y.length || compareSubsets(x, y, elements)
  );
}

function compareSubsets(
  x: readonly string[],
  y: readonly string[],
  order: readonly string[]
): number {
  for (let i = 0; i < Math.min(x.length, y.length); i += 1) {
    const d = order.indexOf(x[i]) - order.indexOf(y[i]);
    if (d !== 0) {
      return d;
    }
  }
  return 0;
}

/** 2ⁿ as an exact integer string for any n. */
export function powerSetSize(n: number): bigint {
  return BigInt(2) ** BigInt(n);
}

export function cartesianProduct(
  a: readonly string[],
  b: readonly string[],
  limit = MAX_PRODUCT_LISTED
): string[] {
  const pairs: string[] = [];
  for (const x of a) {
    for (const y of b) {
      if (pairs.length >= limit) {
        return pairs;
      }
      pairs.push(pairText(x, y));
    }
  }
  return pairs;
}

export interface SetReport {
  a: string[];
  b: string[];
  universe: string[];
  /** Elements of A or B missing from a universe the reader typed; empty otherwise. */
  outside: string[];
  union: string[];
  intersection: string[];
  aMinusB: string[];
  bMinusA: string[];
  symmetric: string[];
  complementA: string[];
  complementB: string[];
  relation:
    | "equal"
    | "a-proper-subset"
    | "b-proper-subset"
    | "disjoint"
    | "overlap";
}

export function compareSets(a: readonly string[], b: readonly string[]) {
  const common = intersection(a, b);
  if (a.length === b.length && common.length === a.length) {
    return "equal" as const;
  }
  if (isSubset(a, b)) {
    return "a-proper-subset" as const;
  }
  if (isSubset(b, a)) {
    return "b-proper-subset" as const;
  }
  return common.length === 0 ? ("disjoint" as const) : ("overlap" as const);
}

/** Everything the calculator tabulates. `universe` null means "A ∪ B". */
export function reportSets(
  a: string[],
  b: string[],
  universe: string[] | null
): SetReport {
  const joined = union(a, b);
  const u = universe ?? joined;
  return {
    a,
    b,
    universe: u,
    outside: universe ? difference(joined, universe) : [],
    union: joined,
    intersection: intersection(a, b),
    aMinusB: difference(a, b),
    bMinusA: difference(b, a),
    symmetric: symmetricDifference(a, b),
    complementA: difference(u, a),
    complementB: difference(u, b),
    relation: compareSets(a, b),
  };
}
